import React, { useMemo, useState } from 'react';
import { prop } from 'ramda';

import { InputsRow } from '@interlayer/js/app/components/common/Fields/Input/components/InputsRow';
import { AddressManuallyFields } from '@interlayer/js/app/components/main/Registration/components/fields/AddressFields/components/AddressManuallyFields';
import { AddressPostCodeFields } from '@interlayer/js/app/components/main/Registration/components/fields/AddressFields/components/AddressPostCodeFields';
import { GenderTabs } from '@interlayer/js/app/components/main/Registration/components/fields';

import { Button } from 'js/components/common/Button';
import ErrorMessage from 'js/components/common/Fields/ErrorMessage';
import { Select } from 'js/components/common/Fields/Select';
import Form from 'js/components/common/Form';

import { useUserSelector } from '@core/js/store/hooks';

import { changeDetails } from '@interlayer/js/app/components/main/Registration/server';
import { useDateWithFullMonth } from '@interlayer/js/app/hooks/useDateWithFullMonth';
import { useValidationRules } from '@interlayer/js/app/hooks/useValidationRules';
import { useClubData } from '@interlayer/js/app/services/clubsService/useClubData';
import { clsx } from '@interlayer/js/app/utils/clsx.util';
import { useFetchWithTwoFa } from '@interlayer/js/components/popups/TwoFactorAuthenticationPopup/buzz/hooks/useFetchWithTwoFa';

import { useTranslations } from 'js/hooks/i18n';
import { useFetchUserDetails } from 'js/hooks/user';
import { useShowNotification } from 'js/hooks/useShowNotification';

import {
    filterFormDataUserDetails
} from './helpers/filterFormDataUserDetails';
import { VALIDATION_FIELDS_CONTACT_INFORMATION } from './constants';

import styleDetails from '../../UserDetails.scss';
import style from './ContactInformation.scss';

export const ContactInformation = () => {
    const t = useTranslations('playtech.mobile.system.userdetails.');
    const validationConfig = useValidationRules(VALIDATION_FIELDS_CONTACT_INFORMATION, 'ContactInformation');
    const userDetails = useUserSelector(prop('userDetails'));
    const fetchUserDetails = useFetchUserDetails();
    const showNotification = useShowNotification();
    const getDateWithFullMonth = useDateWithFullMonth();
    const [{ data: clubList }] = useClubData();
    const fetchWithTwoFa = useFetchWithTwoFa();
    const [isShowManuallyAddress, setIsShowManuallyAddress] = useState(false);

    /* eslint-disable camelcase */
    const clubListOptions = useMemo(() => clubList.clubs?.map(({ club_name, hall_code }) => ({
        value: hall_code,
        label: club_name
    })), [clubList]);

    const inputsRowOptionsName = [
        {
            disabled: true,
            name: 'firstname',
            placeholder: t('label.firstname')
        },
        {
            disabled: true,
            name: 'lastname',
            placeholder: t('label.lastname')
        }
    ];

    const inputsRowDobAndUserName = [
        {
            disabled: true,
            name: 'birthDate',
            placeholder: t('dob')
        },
        {
            disabled: true,
            name: 'username',
            placeholder: t('label.membershipnumber')
        }
    ];

    const inputsRowEmailAndMobile = [
        {
            placeholder: t('emailAddress'),
            name: 'email'
        },
        {
            placeholder: t('mobileNumber'),
            name: 'phone',
            isOnlyNumber: true,
            inputMode: 'numeric',
            pattern: '\\d*'
        }
    ];

    const initialValues = {
        gender: userDetails.title,
        firstname: userDetails.firstName,
        lastname: userDetails.lastName,
        homeVenue: userDetails.homeVenue,
        house: userDetails.addressDetails?.buildingNumber || userDetails.address?.split(' ')[0],
        street: userDetails.addressDetails?.streetName || userDetails.address
            ?.split(' ')
            .slice(1)
            .join(' '),
        city: userDetails.city || '',
        state: userDetails.state || '',
        zip: userDetails.zip,
        phone: userDetails.cellphone || userDetails.phone,
        email: userDetails.email,
        username: userDetails.username,
        birthDate: getDateWithFullMonth(userDetails.birthDate)
    };

    const handlerSubmit = (values) => fetchWithTwoFa({
        fetch: () => changeDetails(filterFormDataUserDetails(values)),
        afterFetch: () => fetchUserDetails()
            .then(() => {
                showNotification(t('message.successUpdate'));
            })
    });

    return (
        <Form
            onSubmit={handlerSubmit}
            validationConfig={validationConfig}
            initialValues={initialValues}
            render={({ submitting, submitError, handleSubmit, pristine, hasValidationErrors }) => (
                <form className={style.container} onSubmit={handleSubmit}>
                    <div className={styleDetails.title}>{t('myDetails')}</div>

                    {submitError && (
                        <ErrorMessage
                            className={styleDetails.error}
                            error={submitError}
                            animate
                            isFormError
                        />
                    )}

                    <GenderTabs />

                    <InputsRow options={inputsRowOptionsName} />

                    <InputsRow options={inputsRowDobAndUserName} />

                    <div className={style.addressTitle}>{t('myDetails.address')}</div>

                    <AddressPostCodeFields
                        isNearestClub={false}
                        handlerSwitchManually={setIsShowManuallyAddress}
                        className={clsx({ hidden: isShowManuallyAddress })}
                        classNames={{
                            addressWrapper: style.addressWrapper,
                            inputWrap: style.inputWrap,
                            addressData: style.addressData
                        }}
                    />

                    <AddressManuallyFields
                        isShowLinkAuto
                        handlerSwitchManually={setIsShowManuallyAddress}
                        className={clsx({ hidden: !isShowManuallyAddress })}
                        classNames={{
                            linkAddressWrap: style.linkAddressWrap
                        }}
                    />

                    <InputsRow options={inputsRowEmailAndMobile} />

                    <Select
                        name="homeVenue"
                        options={clubListOptions}
                        label={t('label.mybingoclub')}
                        classNames={{ control: style.clubName }}
                    />

                    <Button
                        type="submit"
                        disabled={submitting || pristine || hasValidationErrors}
                        className={styleDetails.button}
                    >
                        {t('update.button')}
                    </Button>
                </form>
            )}
        />
    );
};
